import React, { useState, useEffect } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { ISubscriptionItem, IUser, UsersParser } from './models';
import { UsersApi } from '../../api/usersApi';
import { PopupOrPanel } from '../../components/popupsAndPanels/PopupAndPanels';
import FormTextField from 'components/formComponents/FormTextField';
import { FormBooleanField } from 'components/formComponents/FormBooleanField';
import Tooltip from 'components/tooltip/Tooltip';
import { setUpdateUsersList } from 'reducers/misc';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import { useGetDashboardList } from 'pages/dashboard/useGetDashboardList';
import { Spinner } from 'components/spinner/Spinner';
import { useToast } from 'hooks/useToast';

type Props = {
  panelOpen: boolean;
  togglePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const tooltip = (
  <Tooltip text="This person will have access to subscriptions, manage users and manage school" />
);

export const AddUser: React.FC<Props> = ({ panelOpen, togglePanelOpen }) => {
  const [error, setError] = useState<string>('');

  const dispatch = useDispatch();
  const setUpdateList = (status: boolean) =>
    dispatch(setUpdateUsersList(status));

  const handleToast = useToast(
    'This member of your team will now be emailed a password to login. Please ask them to check their junk folder if they do not see this in their inbox.'
  );

  const validationSchemaAddUser = Yup.object({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    emailAddress: Yup.string()
      .email('Please enter a valid email address')
      .required('Email address is required'),
    bMainAccountHolder: Yup.boolean().required('Required field'),
    bGetSet4PE: Yup.boolean().required('Required field'),
    bGetSet4Music: Yup.boolean().required('Required field'),
  });

  const { dashboardListInfo, error: subscriptionsListError } =
    useGetDashboardList();

  const [subscriptionsList, setSubscriptionList] = useState<
    ISubscriptionItem[] | null
  >(null);

  useEffect(() => {
    if (subscriptionsListError) {
      setError(subscriptionsListError.message);
      return;
    }
    if (dashboardListInfo) {
      const subjectsList = dashboardListInfo.map((item) => ({
        label: item.subject,
        fieldName: `b${item.subject.split(' ').join('')}`,
      }));
      setSubscriptionList(subjectsList);
    }
  }, [dashboardListInfo, subscriptionsListError]);

  const handleSubmitAddUser = async (
    values: IUser,
    { setSubmitting }: FormikHelpers<IUser>
  ) => {
    setError('');
    setSubmitting(true);
    const result = await UsersApi.submitUser(null, values);

    if (result.data) {
      setSubmitting(false);
      setUpdateList(true);
      togglePanelOpen(false);
      handleToast();
    }

    if (result.error) {
      const error = result.error.message;
      setError(error);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={UsersParser.createEmptyUser()}
      validationSchema={validationSchemaAddUser}
      onSubmit={handleSubmitAddUser}
    >
      {({ values, submitForm, setFieldValue, isSubmitting }) => (
        <PopupOrPanel
          isPopup={false}
          isOpen={panelOpen}
          position="right"
          hdrTitle="Add new user"
          primaryBtnTxt="Save"
          secondaryBtnTxt="Cancel"
          givenClass="TestPanelClass"
          handleClose={() => togglePanelOpen(false)}
          handlePrimaryBtn={submitForm}
          handleSecondaryBtn={() => togglePanelOpen(false)}
          isSubmitting={isSubmitting}
        >
          {subscriptionsList ? (
            <Form>
              <FormGeneralError message={error} />
              <FormTextField
                label="First name"
                field="firstName"
                labelPosition="left"
                required={true}
                optionalTag={'optional'}
                value={values.firstName}
              />
              <FormTextField
                label="Last name"
                field="lastName"
                labelPosition="left"
                required={true}
                optionalTag={'optional'}
                value={values.lastName}
              />
              <FormTextField
                label="Email address"
                field="emailAddress"
                labelPosition="left"
                required={true}
                optionalTag={'optional'}
                value={values.emailAddress}
              />
              <FormBooleanField
                fieldName="bMainAccountHolder"
                label="Main account holder"
                labelPosition="left"
                tooltip={tooltip}
              />

              <h4 className="SpaceTopBtm">Platform access</h4>

              {subscriptionsList.map(
                (item: ISubscriptionItem, index: number) => (
                  <FormBooleanField
                    key={index}
                    fieldName={item.fieldName}
                    label={item.label}
                    labelPosition="left"
                  />
                )
              )}
            </Form>
          ) : subscriptionsListError ? (
            <FormGeneralError message={error} />
          ) : (
            <Spinner positionAbsolute />
          )}
        </PopupOrPanel>
      )}
    </Formik>
  );
};
